import { Select, SelectProps } from 'antd'
import { useMemo, useState, useEffect } from 'react'

import './BalanceSelect.css'

export interface CurrencyBalance {
  id: number
  symbol: string
  name: string
  decimals: number
  iconPath: string
  pngIconPath: string
  totalBalance: number
  avlBalance: number
  depositAndWithdrawEnabled: boolean
  amount: number
}

interface BalanceSelectProps extends Omit<SelectProps, 'options' | 'children'> {
  currencies: CurrencyBalance[]
  onCurrencyChange?: (currency: CurrencyBalance) => void
  totalAvlBalanceUsd?: string | number
}

export const BalanceSelect: React.FC<BalanceSelectProps> = ({
  currencies = [],
  onCurrencyChange,
  totalAvlBalanceUsd,
  value,
  ...selectProps
}) => {
  // Internal state to track selected value if not controlled
  const [internalValue, setInternalValue] = useState<number | string | undefined>(value)

  // Update internal value when prop value changes
  useEffect(() => {
    setInternalValue(value)
  }, [value])

  // Initialize default value on mount
  useEffect(() => {
    if (value === undefined && !internalValue) {
      // Default to Total Avl Balance option if available
      if (totalAvlBalanceUsd !== undefined) {
        setInternalValue('__TOTAL__')
        return
      }

      if (currencies.length > 0) {
        // Default to USD if available, otherwise first currency with balance > 0, or first currency
        const defaultCurrency =
          currencies.find((c) => c.symbol === 'USD') ||
          currencies.find((c) => c.avlBalance > 0) ||
          currencies[0] ||
          null

        if (defaultCurrency) {
          setInternalValue(defaultCurrency.id)
        }
      }
    }
  }, [currencies, value, internalValue, totalAvlBalanceUsd])

  // Find selected currency
  const selectedCurrency = useMemo(() => {
    const currentValue = value !== undefined ? value : internalValue
    if (!currentValue) {
      // Default to USD if available, otherwise first currency with balance > 0, or first currency
      return (
        currencies.find((c) => c.symbol === 'USD') ||
        currencies.find((c) => c.avlBalance > 0) ||
        currencies[0] ||
        null
      )
    }
    return (
      currencies.find((c) => c.id === currentValue || c.symbol === currentValue) ||
      null
    )
  }, [currencies, value, internalValue])

  // Handle currency selection
  const handleChange = (selectedValue: string | number) => {
    // Special handling for the synthetic "Total Avl Balance" option
    if (selectedValue === '__TOTAL__') {
      if (value === undefined) {
        setInternalValue(selectedValue)
      }

      if (selectProps.onChange) {
        selectProps.onChange(selectedValue, null as any)
      }
      return
    }

    const currency = currencies.find(
      (c) => c.id === selectedValue || c.symbol === selectedValue
    )

    if (currency) {
      // Update internal state if not controlled
      if (value === undefined) {
        setInternalValue(selectedValue)
      }

      // Call onCurrencyChange callback
      if (onCurrencyChange) {
        onCurrencyChange(currency)
      }
    }

    // Call original onChange if provided
    if (selectProps.onChange) {
      selectProps.onChange(selectedValue, null as any)
    }
  }

  // Safely parse total available USD balance, defaulting to 0 if invalid
  const displayTotalAvlBalanceUsd = useMemo(() => {
    if (totalAvlBalanceUsd === undefined || totalAvlBalanceUsd === null) {
      return 0
    }
    return totalAvlBalanceUsd || 0
  }, [totalAvlBalanceUsd])

  // Build flat options list (no grouping)
  const options = useMemo(() => {
    const currencyOptions = currencies.map((currency) => {
      const balance = currency?.avlBalance || 0
      const displayText = `${balance} ${currency.symbol}`
      return {
        label: displayText, // This will show in both input and dropdown
        value: currency.id,
        currency,
      }
    })

    if (totalAvlBalanceUsd === undefined) {
      return currencyOptions
    }

    const totalLabel = `Total: ${displayTotalAvlBalanceUsd.toLocaleString(
      undefined,
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    )} USD`

    return [
      {
        label: totalLabel,
        value: '__TOTAL__',
      },
      ...currencyOptions,
    ]
  }, [currencies, totalAvlBalanceUsd, displayTotalAvlBalanceUsd])

  return (
    <div className="balance-select-container">
      <Select
        {...selectProps}
        value={selectedCurrency?.id || value || internalValue}
        onChange={handleChange}
        options={options}
        optionLabelProp="label"
        className="balance-select"
        dropdownClassName="balance-select-dropdown"
        placeholder="Select currency"
        allowClear={false}
      />
    </div>
  )
}

